import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Phone, CheckCircle2 } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { helpAgentStrings } from "@/i18n/helpAgentStrings";
import { langClass } from "@/i18n/langUtils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const HIDDEN_PATHS = new Set(["/login", "/register", "/auth/callback", "/admin"]);

const HelpAgent = () => {
  const { lang, t } = useT();
  const { pathname } = useLocation();
  const h = helpAgentStrings[lang] || helpAgentStrings.en;
  const langCls = langClass(lang);

  const [open, setOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) return undefined;
    const id = setInterval(() => setShowBubble((v) => !v), 3200);
    return () => clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (HIDDEN_PATHS.has(pathname)) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    setOpen(false);
    if (sent) {
      setSent(false);
      setForm({ name: "", email: "", phone: "" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error(h.required);
      return;
    }
    setLoading(true);
    try {
      await api.post("/callback-requests", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      setSent(true);
    } catch {
      toast.error(t.common?.error || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldCls =
    "bg-white/[0.04] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/35";

  return (
    <div
      className={`fixed z-[45] bottom-20 sm:bottom-6 right-4 sm:right-6 flex flex-col items-end gap-2 ${langCls}`}
      data-testid="help-agent"
    >
      {open && (
        <div
          className="w-[min(100vw-2rem,340px)] rounded-2xl border border-white/10 bg-[#0c0c0c]/95 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
          role="dialog"
          aria-label={h.ariaLabel}
          data-testid="help-agent-panel"
        >
          <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 border-b border-white/8">
            <div>
              <div className="font-display text-base font-bold text-white">{h.title}</div>
              <p className="text-xs text-white/55 mt-0.5 leading-relaxed">{h.subtitle}</p>
            </div>
            <button
              type="button"
              onClick={close}
              className="shrink-0 rounded-lg p-1.5 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              aria-label={h.close}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {sent ? (
            <div className="px-4 py-8 text-center" data-testid="help-agent-success">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
              <div className="mt-3 font-semibold text-white">{h.successTitle}</div>
              <p className="mt-1 text-sm text-white/60">{h.successBody}</p>
              <Button
                type="button"
                onClick={close}
                className="mt-5 bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold"
              >
                {h.close}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="px-4 py-4 space-y-3" data-testid="help-agent-form">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono-alt">{h.name}</Label>
                <Input value={form.name} onChange={set("name")} className={`mt-1.5 h-9 ${fieldCls}`} data-testid="help-agent-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono-alt">{h.email}</Label>
                <Input type="email" value={form.email} onChange={set("email")} className={`mt-1.5 h-9 ${fieldCls}`} data-testid="help-agent-email" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono-alt">{h.phone}</Label>
                <Input type="tel" value={form.phone} onChange={set("phone")} className={`mt-1.5 h-9 ${fieldCls}`} data-testid="help-agent-phone" />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold gap-2"
                data-testid="help-agent-submit"
              >
                <Phone className="h-4 w-4" />
                {loading ? h.submitting : h.submit}
              </Button>
            </form>
          )}
        </div>
      )}

      <div className="relative flex items-end gap-2">
        {!open && (
          <div
            className={`help-agent-bubble max-w-[160px] rounded-2xl rounded-br-sm border border-[#D4AF37]/30 bg-[#121212]/95 px-3 py-2 text-xs font-semibold text-white shadow-lg shadow-black/30 transition-all duration-500 ${
              showBubble ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-1 scale-95 pointer-events-none"
            }`}
            aria-hidden="true"
          >
            {h.bubble}
          </div>
        )}

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`help-agent-avatar group relative h-14 w-14 rounded-full border-2 border-[#D4AF37]/50 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] shadow-xl shadow-[#D4AF37]/15 flex items-center justify-center transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] ${
            open ? "ring-2 ring-[#D4AF37]/60" : ""
          }`}
          aria-label={h.ariaLabel}
          aria-expanded={open}
          data-testid="help-agent-toggle"
        >
          <span className="absolute inset-0 rounded-full bg-[#D4AF37]/10 animate-ping opacity-40 group-hover:opacity-60" style={{ animationDuration: "2.5s" }} />
          <svg viewBox="0 0 64 64" className="relative h-9 w-9" aria-hidden="true">
            <circle cx="32" cy="22" r="12" fill="#D4AF37" opacity="0.9" />
            <ellipse cx="32" cy="52" rx="18" ry="14" fill="#D4AF37" opacity="0.75" />
            <circle cx="27" cy="20" r="2" fill="#0B0B0B" />
            <circle cx="37" cy="20" r="2" fill="#0B0B0B" />
            <path d="M26 26 Q32 30 38 26" stroke="#0B0B0B" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default HelpAgent;
